<template>
  <div class="form-field form-field-list">
    <label :for="field.name">
      {{ label }}
    </label>
    <div class="form-field-input-wrapper">
      <input
        :id="field.name"
        ref="el"
        v-model="inputText"
        :name="field.name"
        type="text"
        role="combobox"
        autocomplete="off"
        autocapitalize="none"
        data-selection-appearance="text-input"
        aria-autocomplete="list"
        aria-expanded="false"
        @focus="isFocused = true"
        @blur="handleBlur"
        @keydown.down.prevent="handleKeyDown"
        @keydown.up.prevent="handleKeyUp"
        @keydown.enter.prevent="handleKeyEnter"
      />
      <FormButtonFormFieldButtonReset
        v-if="inputText"
        @click="handleClickReset"
      />
      <span v-else class="field-input-icon">
        <icon name="unrollContent" />
      </span>
    </div>

    <FormFieldSelectListOptions
      v-model="currentIndex"
      :opened="optionsOpened"
      :options="filteredOptions"
      @update="(index) => (currentIndex = index)"
      @select="(index) => selectIndex(index)"
    />
  </div>
</template>
<script setup lang="ts">
interface Props {
  field: FormField;
}
const props = defineProps({
  field: null
});
const inputText = ref<string>('');

const {
  formFieldsData: { memberValues },
} = inject(INJECT_FORM_PAGE);

const fieldValue = computed({
  get() {
    return memberValues.value[props.field.name].value;
  },
  set(value) {
    memberValues.value[props.field.name].value = value;
  },
});

const el = ref(null);
const isFocused = ref<boolean>(false);
const currentIndex = ref<number>(-1);

const label = computed(() => {
  return isFieldRequired(props.field)
    ? `${props.field.label}*`
    : props.field.label;
});

const removeDiacritics = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036F]/g, '');
};

const simplifiedOptions = computed(() => {
  return props.field.options.map((option) => {
    return {
      ...option,
      simplifiedName: removeDiacritics(option.name.toLowerCase()),
    };
  });
});

const filteredOptions = computed(() => {
  const text = inputText.value ? inputText.value : '';
  const regExp = new RegExp(removeDiacritics(text.toLowerCase()));
  return simplifiedOptions.value.filter((option) => {
    return regExp.test(option.simplifiedName.toLowerCase());
  });
});

watch(
  () => filteredOptions,
  () => (currentIndex.value = 0)
);

const selectIndex = (index) => {
  if (index > -1) {
    const selectedOption = { ...filteredOptions.value[index] };
    fieldValue.value = selectedOption.value;
    inputText.value = selectedOption.name;
  } else {
    fieldValue.value = null;
    inputText.value = '';
  }
};

const optionsOpened = computed(() => {
  if (!isFocused.value) return false;
  if (fieldValue.value !== null) return false;
  if (filteredOptions.value.length === 0) return false;
  return true;
});

if (fieldValue.value !== null) {
  const option = props.field.options.find(
    (option) => option.value === fieldValue.value
  );
  if (option) {
    inputText.value = option.name;
  } else {
    fieldValue.value = null;
  }
}

watchEffect(() => {
  if (fieldValue.value) {
    const option = props.field.options.find(
      (option) => option.name === inputText.value
    );
    if (!option) {
      fieldValue.value = null;
    }
  }
});

const handleBlur = () => {
  isFocused.value = false;
};
const handleKeyDown = () => {
  if (currentIndex.value === filteredOptions.value.length - 1) return;
  currentIndex.value += 1;
};
const handleKeyUp = () => {
  if (currentIndex.value === 0) return;
  currentIndex.value -= 1;
};
const handleKeyEnter = () => {
  el.value.blur();
};

const handleClickReset = (e) => {
  fieldValue.value = null;
  inputText.value = '';
};

watch(
  () => isFocused.value === false,
  (focusedOut) => {
    if (!focusedOut) return;
    if (filteredOptions.value.length === 0) {
      selectIndex(-1);
    } else if (
      filteredOptions.value.length === 1 &&
      fieldValue.value === filteredOptions.value[0].value
    ) {
      selectIndex(0);
    } else {
      selectIndex(currentIndex.value);
    }
  }
);
</script>
<style lang="scss" scoped>
input {
  padding-right: 4rem;
}
.form-field {
  position: relative;
}
.form-field-input-wrapper {
  position: relative;
  width: 100%;
  input {
    width: 100%;
  }
}
.form-field-dropbox-wrapper {
  position: absolute;
  z-index: 2;
  top: calc(100% + 1rem);
  width: 100%;
  max-height: 20rem;
  overflow-y: scroll;
}

.field-input-icon {
  display: flex;
  position: absolute;
  right: 0;
  bottom: 0;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 100%;
  pointer-events: none;
  .icon {
    color: var(--color_neutral_200);
  }
}
</style>
